import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { getLogger } from '@packages/core/logging';
import { Button, Icon } from '@packages/ui/shared';
import { otpwService } from '@web/services/singletons';

const logger = getLogger('web');

interface AuthorizationCodeModalProps {
    show: boolean;
    onHide: () => void;
    vin: string;
    expiresAt: Date;
    otpwAssignmentId: string;
}

const useStyles = createUseStyles({
    modalBody: {
        padding: 24,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },
    errorText: {
        color: '#DC3545',
        fontFamily: 'Roboto',
        fontSize: 15,
        textAlign: 'center',
    },
    description: {
        fontFamily: 'Roboto',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '160%',
        color: '#071424',
        marginBottom: 20,
    },
    boldText: {
        color: '#4C525D',
        fontFamily: 'Roboto',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '160%',
    },
    codeContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 20,
    },
    codeInput: {
        flex: 1,
        padding: '12px 16px',
        backgroundColor: '#F0F2F5',
        borderRadius: 8,
        border: 'none',
        fontFamily: 'Roboto',
        fontSize: 15,
        fontWeight: 400,
        color: '#071424',
    },
    buttonContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
    },
    footerText: {
        fontFamily: 'Roboto',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '160%',
        color: '#071424',
    },
});

const formatExpiryDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
};

const formatExpiryTime = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };
    return date.toLocaleTimeString('en-US', options).toLowerCase().replace(' ', ' ');
};

export const AuthorizationCodeModal: FC<AuthorizationCodeModalProps> = ({
    show,
    onHide,
    vin,
    expiresAt,
    otpwAssignmentId,
}) => {
    const classes = useStyles();
    const abortControllerRef = useRef<AbortController | null>(null);
    const [copied, setCopied] = useState(false);
    const [authorizationCode, setAuthorizationCode] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchAuthorizationCode = useCallback(async () => {
        if (!otpwAssignmentId) {
            setError('No assignment ID available');
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        setIsLoading(true);
        setError(null);

        try {
            const response = await otpwService.fetchOtpwAssignment(otpwAssignmentId, abortControllerRef.current.signal);

            if (!response.success) {
                if (!response.aborted) {
                    setError('Failed to fetch authorization code');
                }
                return;
            }

            setAuthorizationCode(response.data.otpw);
        } catch (err) {
            logger.error(`Failed to fetch the authorization code [${otpwAssignmentId}]`, {
                error: err,
                otpwAssignmentId,
            });
            setError('Failed to fetch authorization code');
        } finally {
            setIsLoading(false);
        }
    }, [otpwAssignmentId]);

    useEffect(() => {
        if (show && otpwAssignmentId) {
            fetchAuthorizationCode();
        } else if (!show) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            setAuthorizationCode('');
            setError(null);
            setCopied(false);
        }
    }, [show, otpwAssignmentId]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        if (!show) return;

        const expiryTime = expiresAt.getTime();
        const modalCloseTime = expiryTime - 2000;

        const checkExpiry = () => {
            if (Date.now() >= modalCloseTime) {
                onHide();
            }
        };

        checkExpiry();
        const interval = setInterval(checkExpiry, 500);

        return () => clearInterval(interval);
    }, [show, expiresAt, onHide]);

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(authorizationCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            logger.error('Failed to copy the authorization code', { error: err });
        }
    };

    const handleHide = () => {
        setCopied(false);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Authorization code</Modal.Title>
            </Modal.Header>
            <Modal.Body className={classes.modalBody}>
                {isLoading ? (
                    <div className={classes.loadingContainer}>
                        <Spinner animation="border" />
                    </div>
                ) : error ? (
                    <div className={classes.loadingContainer}>
                        <p className={classes.errorText}>{error}</p>
                    </div>
                ) : (
                    <>
                        <p className={classes.description}>
                            Use it to reset the car keys for VIN Session <span className={classes.boldText}>{vin}</span>
                            .
                            <br />
                            Note that it will expire on{' '}
                            <span className={classes.boldText}>
                                {formatExpiryDate(expiresAt)}, at {formatExpiryTime(expiresAt)}
                            </span>
                            .
                        </p>
                        <div className={classes.codeContainer}>
                            <input type="text" value={authorizationCode} readOnly className={classes.codeInput} />
                            <Button variant="primary" onPress={handleCopyCode} style={{ minWidth: 130 }}>
                                <span className={classes.buttonContent}>
                                    {copied ? (
                                        <>
                                            <Icon name="checkmark" size={16} color="white" style={{ marginRight: 8 }} />
                                            Copied
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="copy" size={16} color="white" style={{ marginRight: 8 }} />
                                            Copy code
                                        </>
                                    )}
                                </span>
                            </Button>
                        </div>
                        <p className={classes.footerText}>If you have any issues, contact your manager.</p>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};
